import { type TutorialId, TUTORIALS } from '@/features/tutorial/lib/tutorials'

/** Number of consecutive acknowledged steps. Absent = never opened, 0 = opened. */
export type TutorialProgress = Partial<Record<TutorialId, number>>

export type TutorialStatus = 'new' | 'started' | 'done'

export interface TutorialState {
  status: TutorialStatus
  /** Acknowledged steps, for "2 of 3" and the first unfinished step to resume. */
  seen: number
  total: number
}

export function tutorialState(progress: TutorialProgress, id: TutorialId): TutorialState {
  const total = TUTORIALS[id].steps.length
  const acknowledged = progress[id]
  if (acknowledged === undefined) return { status: 'new', seen: 0, total }
  const seen = Math.min(total, Math.max(0, acknowledged))
  return { status: seen >= total ? 'done' : 'started', seen, total }
}

/** Opening a tour never acknowledges its current step, even when opened on the last one. */
export function startTutorial(progress: TutorialProgress, id: TutorialId): TutorialProgress {
  return progress[id] === undefined ? { ...progress, [id]: 0 } : progress
}

/** Acknowledge the next unfinished step. Jumping ahead or skipping cannot complete a tour. */
export function recordTutorialStep(
  progress: TutorialProgress,
  id: TutorialId,
  step: number
): TutorialProgress {
  const count = progress[id] ?? 0
  if (step !== count || step >= TUTORIALS[id].steps.length) return progress
  return { ...progress, [id]: count + 1 }
}

/** The hub's entries: every registered tutorial, plus the welcome and preset tours. */
export type HubEntryId = TutorialId | 'welcome' | 'preset'

export type ChapterKey = 'start' | 'trading' | 'orderTypes' | 'risk' | 'research' | 'crossVenue'

/**
 * The hub's table of contents, most important first: what every user needs on day one, then
 * placing orders, the algorithmic order types, risk, research, and the cross-venue tools.
 * Every tutorial appears exactly once (tested).
 */
export const TUTORIAL_CHAPTERS: readonly { key: ChapterKey; entries: readonly HubEntryId[] }[] = [
  {
    key: 'start',
    entries: [
      'welcome',
      'chat-order-form',
      'watchlist',
      'price-chart',
      'positions',
      'account-cards',
    ],
  },
  {
    key: 'trading',
    entries: [
      'order-book',
      'quick-order',
      'one-tap-buy',
      'preset-manager',
      'preset',
      'trades',
      'ticker',
    ],
  },
  { key: 'orderTypes', entries: ['twap', 'limit-chase', 'advanced-twap', 'rfq', 'spreader-algo'] },
  { key: 'risk', entries: ['risk-slides', 'risk-slides-page', 'hedge'] },
  { key: 'research', entries: ['assistant', 'screener', 'newsfeed', 'economic-calendar', 'data'] },
  { key: 'crossVenue', entries: ['arb', 'spreader'] },
]

export function isTutorialEntry(id: HubEntryId): id is TutorialId {
  return id !== 'welcome' && id !== 'preset'
}

export function completedCount(progress: TutorialProgress): number {
  return (Object.keys(TUTORIALS) as TutorialId[]).filter(
    (id) => tutorialState(progress, id).status === 'done'
  ).length
}
